import {
  updateCables,
  updateGroupedConnections,
  updateGroupedConnectionsChildren,
  updateGroupedConnectionPoles,
  UpdateChild,
  UpdateState,
} from "../../app/networkSlice";
import { AppDispatch } from "../../app/store";
import { transformerTypes } from "../../constants/groupUpdateTypes";
import * as statusConstants from "../../constants/status";
import { ClientSettings } from "../../model/reference/settingsTypes";
import { GroupedConnection } from "../../model/viewModel/groupedConnection";
import { GroupedConnectionPoint } from "../../model/viewModel/groupedConnectionPoint";
import { RingfencedElement } from "../../model/viewModel/ringfencedElement";

export const shouldShowLinkBoxStatusForm = (
  clientSettings: ClientSettings,
  selected: string,
  ringfencedFiltered: RingfencedElement[],
): boolean => {
  if (!clientSettings.features.ConfigurableLinkBoxEnabled) {
    return false;
  }
  const shouldShow = (type: string) => {
    switch (type) {
      case "Nodes":
      case "Node":
      case "Service End Node":
        return true;
      default:
        return false;
    }
  };

  if (selected === "All") {
    return false;
  }
  return shouldShow(selected) && ringfencedFiltered.some((p) => p.linkBox);
};

export const shouldShowPotEndStatusForm = (
  clientSettings: ClientSettings,
  selected: string,
  ringfencedFiltered: RingfencedElement[],
): boolean => {
  if (!clientSettings.features.CostingEnabled) {
    return false;
  }
  const shouldShow = (type: string) => {
    switch (type) {
      case "Nodes":
      case "Node":
      case "Service End Node":
        return true;
      default:
        return false;
    }
  };

  if (selected === "All") {
    return false;
  }
  return shouldShow(selected) && ringfencedFiltered.some((p) => p.potEnds?.length > 0);
};

export const shouldShowPoleStatusForm = (
  clientSettings: ClientSettings,
  selected: string,
  ringfencedFiltered: RingfencedElement[],
): boolean => {
  if (!clientSettings.features.CostingEnabled) {
    return false;
  }
  const shouldShow = (type: string) => {
    switch (type) {
      case "Nodes":
      case "Node":
      case "Service End Node":
        return true;
      default:
        return false;
    }
  };

  if (selected === "All") {
    return false;
  }
  return shouldShow(selected) && ringfencedFiltered.some((p) => p.pole?.enabled);
};

export const shouldShowStatusForm = (
  clientSettings: ClientSettings,
  selected: string,
  ringfencedTypes: RingfencedElement[],
): boolean => {
  if (!clientSettings.features.NewExistingStatusEnabled) {
    return false;
  }
  const shouldShow = (type: string) => {
    switch (type) {
      case "Properties":
      case "Domestic Property":
      case "Small Commercial Property":
      case "Large Commercial Property":
      case "Industrial Property":
      case "Conductors":
      case "Mains Overhead Line":
      case "Mains Underground Cable":
      case "Service Overhead Line":
      case "Service Underground Cable":
      case "LCT":
      case "Generator":
      case "Heat Pump":
      case "EV Chargepoint":
      case "Connection":
      case "Grouped Connection":
        return true;
      default:
        return false;
    }
  };

  if (selected === "All") {
    const isNotAllowed = ringfencedTypes.some(
      (p) =>
        !shouldShow(p.name) &&
        !["Node", "Service End Node"].includes(p.name) &&
        !transformerTypes.includes(p.name),
    );
    return !isNotAllowed;
  }
  return shouldShow(selected);
};

export const reviewStatusChange = (
  ringfencedFiltered: RingfencedElement[],
  cableType: string | undefined,
  status: string | undefined,
  ohWhitelist: string[],
  ugWhitelist: string[],
): { name: string; count: number }[] => {
  const warnings: { [key: string]: number } = {};
  if (cableType) {
    return [];
  }
  ringfencedFiltered.forEach((p) => {
    switch (p.styles.name) {
      case "Overhead Line":
      case "Underground Cable":
        if (status === statusConstants.NEW || status === statusConstants.REPLACEMENT) {
          const whitelist = p.cableGroup.split("-")[1] === "overhead" ? ohWhitelist : ugWhitelist;
          if (!whitelist.includes(p.cableType) && p.cableType !== "auto") {
            const message = `The cable type for {0} ${
              p.cableGroup.split("-")[1]
            } cable{1} will be set to Auto`;
            warnings[message] = (warnings[message] || 0) + 1;
          }
        }
        break;
    }
  });
  return Object.entries(warnings).map(([name, count]) => ({ name, count }));
};

export const applyStatusChange = (
  ringfencedFiltered: RingfencedElement[],
  dispatchRedux: AppDispatch,
  status: string,
  ohWhitelist: string[],
  ugWhitelist: string[],
  defaultGroundType: string,
  groupedConnections: GroupedConnection[],
): void => {
  const childUpdates: UpdateChild[] = [];

  ringfencedFiltered.forEach((p) => {
    if (transformerTypes.includes(p.styles.name)) {
      return;
    }

    const pushUpdate = (point: GroupedConnectionPoint) => {
      if (point.status !== status) {
        childUpdates.push({
          id: p.id,
          childUpdate: { id: point.id, name: "status", value: status },
          isGroupUpdate: false,
          parentId: null,
        });
      }
    };

    if (p.groupedConnectionPoints?.length > 0) {
      if (p.ringfencedPoint) {
        pushUpdate(p.ringfencedPoint);
      } else {
        p.groupedConnectionPoints.forEach((q) => {
          pushUpdate(q);
        });
      }
    }
  });

  if (childUpdates.length) {
    dispatchRedux(updateGroupedConnectionsChildren(childUpdates));
  }

  const getUpdate = (assetId: string, status: string): UpdateState => {
    return {
      id: assetId,
      name: "status",
      value: status,
    };
  };

  const assetGroups: any = ringfencedFiltered.reduce(
    (accumulator, currentValue) => {
      const name = currentValue.styles.name;
      accumulator[name] = [...(accumulator[name] || []), currentValue];
      return accumulator;
    },
    {} as Record<string, RingfencedElement[]>,
  );

  const allCables: RingfencedElement[] = (assetGroups["Overhead Line"] || []).concat(
    assetGroups["Underground Cable"] || [],
  );

  if (allCables.length > 0) {
    const updateStates = allCables.map((a) => getUpdate(a.id, status));
    dispatchRedux(updateCables(updateStates));
  }

  const connectedPoles: GroupedConnection[] = allCables
    .filter((c) => c.cableGroup === "mains-overhead")
    .flatMap((c) => {
      return groupedConnections.filter(
        (gc) => (gc.id === c.startAssetId || gc.id === c.endAssetId) && gc.pole,
      );
    });

  if (connectedPoles.length > 0) {
    const updateStates = connectedPoles.map((a) => getUpdate(a.id, status));
    dispatchRedux(updateGroupedConnectionPoles(updateStates));
  }

  if (status === statusConstants.NEW || status === statusConstants.REPLACEMENT) {
    const getGroundTypeUpdate = (cable: RingfencedElement): UpdateState => {
      return {
        id: cable.id,
        name: "groundTypeOverrides",
        value: [
          {
            groundType: defaultGroundType,
            addApplied: false,
            length: cable.overrideLength > 0 ? cable.overrideLength : cable.length,
          },
        ],
      };
    };

    const updateCableStates = allCables
      .filter((c) => !c.groundTypeOverrides || !c.groundTypeOverrides.length)
      .map((c) => getGroundTypeUpdate(c));

    if (updateCableStates.length > 0) {
      dispatchRedux(updateCables(updateCableStates));
    }
  }

  if (status === statusConstants.NEW || status === statusConstants.REPLACEMENT) {
    const getAutoselectUpdates = (cable: RingfencedElement): UpdateState[] => {
      const whitelist = cable.cableGroup.split("-")[1] === "overhead" ? ohWhitelist : ugWhitelist;

      if (whitelist.includes(cable.cableType)) {
        return [];
      } else {
        return [
          {
            id: cable.id,
            name: "autoSelect",
            value: true,
          },
          {
            id: cable.id,
            name: "cableType",
            value: "auto",
          },
        ];
      }
    };

    const updateCableStates = allCables.flatMap((c) => getAutoselectUpdates(c));

    if (updateCableStates.length > 0) {
      dispatchRedux(updateCables(updateCableStates));
    }
  }

  const allConsumers: RingfencedElement[] = (assetGroups["Domestic Property"] || [])
    .concat(assetGroups["Small Commercial Property"] || [])
    .concat(assetGroups["Large Commercial Property"] || [])
    .concat(assetGroups["Industrial Property"] || [])
    .concat(assetGroups["Generator"] || [])
    .concat(assetGroups["Heat Pump"] || [])
    .concat(assetGroups["EV Chargepoint"] || [])
    .concat(assetGroups["Grouped Connection"] || []);

  if (allConsumers.length > 0) {
    const updateStates = allConsumers.map((c) => getUpdate(c.id, status));
    dispatchRedux(updateGroupedConnections(updateStates));
  }

  const allNodes: RingfencedElement[] = (assetGroups["Node"] || []).concat(
    assetGroups["Service End Node"] || [],
  );

  if (allNodes.length > 0) {
    const getNodeUpdates = (node: RingfencedElement): UpdateState[] => {
      const updateStates: UpdateState[] = [];

      if (node.pole && node.pole !== status) {
        updateStates.push({
          id: node.id,
          name: "pole",
          value: { ...node.pole, status },
        });
      }
      if (node.potEnds && node.potEnds.some((q: any) => q.status !== status)) {
        updateStates.push({
          id: node.id,
          name: "potEnds",
          value: node.potEnds.map((q: any) => ({ ...q, status })),
        });
      }
      if (node.linkBox && node.linkBox !== status) {
        updateStates.push({
          id: node.id,
          name: "linkBox",
          value: { ...node.linkBox, status },
        });
      }

      return updateStates;
    };

    const updateStates = allNodes.flatMap((r) => getNodeUpdates(r));
    if (updateStates.length > 0) {
      dispatchRedux(updateGroupedConnections(updateStates));
    }
  }
};
